import React, { useEffect, useState } from "react";
import { Button, Card, Flex, Popconfirm, Radio, Select, Space, Typography, message, theme } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { Channel, ChannelAgent, conversationsApi } from "@/services/conversations.api";
import { StoreSeller, storePopulationsApi } from "@/services/store-populations.api";
import { isCloudApi } from "@/services/channel-types";
import { getErrorMessage } from "@/utilities";
import { AgentSummary, agentApi } from "@/pages/stores/agent/api";
import { asistentePorDefecto, nombreDelAsistente, opcionesDelCanal } from "@/pages/stores/agent/asistentes";
import { MODOS, ModoDelCanal, esAdminDelStore, guardadoDelAgente, modoDe, modoPermitido } from "./channel-agent";

const { Text } = Typography;

/**
 * El asistente IA del canal (tareas 5.2 y 5.11, D29 y D64): su modo `off | assist | auto` y quién
 * atiende en automático, entre los asistentes del store. Su nombre sale de la config del asistente.
 * Lo cambia sólo el admin del Store; el resto lo ve.
 */
export const ChannelAgentCard: React.FC<{ channel: Channel }> = ({ channel }) => {
  const { token } = theme.useToken();
  const { data: identity } = useGetIdentity<any>();
  const editable = esAdminDelStore(identity, channel.storeCode);
  const esCloud = isCloudApi(channel.type);

  const [actual, setActual] = useState<ChannelAgent | null>(null);
  const [asistentes, setAsistentes] = useState<AgentSummary[]>([]);
  const [modo, setModo] = useState<ModoDelCanal>("off");
  const [userId, setUserId] = useState<number | null>(null);
  // D58: el seller por el que vende el canal. Vacío: por el store.
  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [sellerCode, setSellerCode] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [saving, setSaving] = useState(false);

  const aplicar = (a: ChannelAgent | null) => {
    setActual(a);
    setModo(modoDe(a));
    if (a) {
      setUserId(a.userId);
      setSellerCode(a.sellerCode ?? null);
    }
  };

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    Promise.all([
      conversationsApi.getChannelAgent(channel.channelId).catch(() => null),
      agentApi.asistentes(channel.storeCode).catch(() => [] as AgentSummary[]),
      storePopulationsApi.listSellers(channel.storeCode).catch(() => [] as StoreSeller[]),
    ])
      .then(([a, lista, sel]) => {
        if (!vivo) return;
        setAsistentes(lista);
        setSellers(sel);
        aplicar(a);
        // En un alta se propone el asistente si no hay a quién más elegir.
        if (!a) setUserId(asistentePorDefecto(lista));
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [channel.channelId, channel.storeCode]);

  const elegido = asistentes.find((a) => a.userId === userId);
  // D64: el `display_name` del canal queda sin uso, pero la API lo pide: va el de la config.
  const nombre = elegido ? nombreDelAsistente(elegido) : actual?.userId === userId ? actual?.displayName ?? "" : "";

  const guardar = async () => {
    const g = guardadoDelAgente(modo, { userId, displayName: nombre, sellerCode }, actual, esCloud);
    if (g.accion === "nada") {
      message.info("No hay cambios en el asistente");
      return;
    }
    if (g.accion === "error") {
      message.error(g.motivo);
      return;
    }
    setSaving(true);
    try {
      aplicar(await conversationsApi.setChannelAgent(channel.channelId, g.body));
      message.success(g.body.enabled ? "Asistente guardado" : "Asistente apagado en este canal");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const quitar = async () => {
    setSaving(true);
    try {
      await conversationsApi.removeChannelAgent(channel.channelId);
      setActual(null);
      setModo("off");
      message.success("Asistente quitado del canal");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const ayuda = MODOS.find((m) => m.value === modo)?.ayuda;
  const delCanal = asistentes.find((a) => a.userId === actual?.userId);

  return (
    <Card
      loading={cargando}
      title={<span style={{ fontSize: 14 }}>Asistente IA</span>}
      style={{ marginBottom: 16, borderColor: token.colorBorder }}
      styles={{ header: { minHeight: 38, padding: "0 16px" } }}
    >
      {editable ? (
        <Flex vertical gap={12} style={{ maxWidth: 560 }}>
          <Radio.Group value={modo} onChange={(e) => setModo(e.target.value)}>
            {MODOS.map((m) => (
              <Radio.Button key={m.value} value={m.value} disabled={!modoPermitido(m.value, esCloud)}>
                {m.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          <Text type="secondary">
            {ayuda}
            {!esCloud && " El modo automático es sólo para canales de WhatsApp Cloud API."}
          </Text>

          {modo !== "off" && (
            <>
              <Flex vertical gap={4}>
                <Text>Asistente del canal</Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  value={userId ?? undefined}
                  onChange={setUserId}
                  placeholder="El que atiende en automático"
                  options={opcionesDelCanal(asistentes, actual)}
                  notFoundContent="Este Store no tiene asistentes"
                />
                <Text type="secondary">
                  {asistentes.length === 0
                    ? "Un asistente es un operador del Store con el permiso de asistente; se configura en Asistente IA."
                    : "Es el que le responde al cliente en automático, con su nombre y su configuración. En las notas se puede arrobar a cualquier asistente encendido del Store."}
                </Text>
              </Flex>
              {sellers.length > 1 && (
                <Flex vertical gap={4}>
                  <Text>Sucursal del canal</Text>
                  <Select
                    allowClear
                    value={sellerCode ?? undefined}
                    onChange={(v) => setSellerCode(v ?? null)}
                    placeholder="Todas las sucursales del store"
                    options={sellers.map((s) => ({ value: s.sellerCode, label: s.name }))}
                  />
                  <Text type="secondary">
                    Con una sucursal, el asistente responde sólo con sus datos, su precio y su stock, y arma los
                    presupuestos con ella. Lo de otra sucursal lo pasa a una persona.
                  </Text>
                </Flex>
              )}
            </>
          )}

          <Space>
            <Button type="primary" icon={<CheckOutlined />} iconPosition="end" loading={saving} onClick={guardar}>
              Guardar asistente
            </Button>
            {actual && (
              <Popconfirm
                title="¿Quitar el asistente de este canal?"
                description="Se borra su configuración en el canal. Para pausarlo alcanza con Apagado."
                okText="Quitar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={quitar}
              >
                <Button danger disabled={saving}>
                  Quitar
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Flex>
      ) : (
        <Flex vertical gap={4}>
          <Text>
            {MODOS.find((m) => m.value === modoDe(actual))?.label}
            {actual && ` · ${delCanal ? nombreDelAsistente(delCanal) : actual.displayName}`}
            {actual?.sellerCode &&
              ` · ${sellers.find((s) => s.sellerCode === actual.sellerCode)?.name ?? actual.sellerCode}`}
          </Text>
          <Text type="secondary">Sólo el administrador del store puede cambiarlo.</Text>
        </Flex>
      )}
    </Card>
  );
};
